import { cva } from '@/styled-system/css';

export const toggleCardTriggerRecipe = cva({
  base: {
    appearance: 'none',
    borderRadius: 'inherit',
    display: 'block',
    inset: 0,
    position: 'absolute',
    transitionProperty: {
      _motionReduce: 'none',
      _motionSafe: 'box-shadow, background-color',
    },
    transitionDuration: {
      _motionReduce: '0s',
      _motionSafe: '{durations.200}, {durations.150}',
    },
    transitionTimingFunction: '{easings.ease-in}, {easings.ease-out}',
    zIndex: 0,

    _active: {
      transitionDuration: '0s',

      _after: {
        transitionDuration: '0s',
      },
    },

    _after: {
      content: '""',
      position: 'absolute',
      inset: 0,
      borderRadius: 'inherit',
      pointerEvents: 'none',
      transitionProperty: {
        _motionReduce: 'none',
        _motionSafe: 'border-color',
      },
      transitionDuration: {
        _motionReduce: '0s',
        _motionSafe: '{durations.150}',
      },
      transitionTimingFunction: '{easings.ease-out}',
    },

    _focusVisible: {
      boxShadow: 'focus',
    },
  },
  variants: {
    disabled: {
      true: {
        boxShadow: 'none',
      },
      false: {},
    },
    elevated: {
      true: {},
      false: {},
    },
    outlined: {
      true: {},
      false: {},
    },
    selected: {
      true: {
        _after: {
          borderWidth: 'ToggleCard.Root.selected',
          borderStyle: 'solid',
          borderColor: 'border.action.hero.selected.default',
        },
        _disabled: {
          _after: {
            borderColor: 'border.action.hero.selected.default/40',
          },
        },
        _notDisabled: {
          _hover: {
            _after: {
              borderColor: 'border.action.hero.selected.hover',
            },
          },
          _active: {
            _after: {
              borderColor: 'border.action.hero.selected.pressed',
            },
          },
        },
      },
      false: {},
    },
    tone: {
      neutral: {},
      soft: {},
    },
  },
  compoundVariants: [
    {
      selected: false,
      outlined: true,
      css: {
        _after: {
          borderWidth: 'default',
          borderStyle: 'solid',
          borderColor: 'ToggleCard.Root.neutral.border.default',
        },
        _disabled: {
          _after: {
            borderColor: 'ToggleCard.Root.neutral.border.default/40',
          },
        },
        _notDisabled: {
          _hover: {
            _after: {
              borderColor: 'ToggleCard.Root.neutral.border.hover',
            },
          },
          _active: {
            _after: {
              borderColor: 'ToggleCard.Root.neutral.border.pressed',
            },
          },
        },
      },
    },
    {
      elevated: true,
      selected: false,
      disabled: false,
      css: {
        boxShadow: {
          base: 'ToggleCard.Root.default',
          _hover: 'ToggleCard.Root.hover',
          _active: 'none',
        },
      },
    },
    {
      elevated: true,
      selected: true,
      disabled: false,
      css: {
        boxShadow: {
          _hover: 'ToggleCard.Root.hover',
          _active: 'none',
        },
      },
    },
    {
      elevated: false,
      tone: 'neutral',
      css: {
        backgroundColor: {
          base: 'ToggleCard.Root.neutral.bg.default',
          _disabled: 'ToggleCard.Root.neutral.bg.default/40',
          _notDisabled: {
            _hover: 'ToggleCard.Root.neutral.bg.hover',
            _active: 'ToggleCard.Root.neutral.bg.pressed',
          },
        },
      },
    },
    {
      elevated: false,
      tone: 'soft',
      css: {
        backgroundColor: {
          base: 'ToggleCard.Root.soft.bg.default',
          _disabled: 'ToggleCard.Root.soft.bg.default/40',
          _notDisabled: {
            _hover: 'ToggleCard.Root.soft.bg.hover',
            _active: 'ToggleCard.Root.soft.bg.pressed',
          },
        },
      },
    },
    {
      elevated: true,
      tone: 'neutral',
      css: {
        backgroundColor: {
          base: 'ToggleCard.Root.neutral.elevated.bg.default',
          _disabled: 'ToggleCard.Root.neutral.elevated.bg.default/40',
          _notDisabled: {
            _hover: 'ToggleCard.Root.neutral.elevated.bg.hover',
            _active: 'ToggleCard.Root.neutral.elevated.bg.pressed',
          },
        },
      },
    },
    {
      elevated: true,
      tone: 'soft',
      css: {
        backgroundColor: {
          base: 'ToggleCard.Root.soft.elevated.bg.default',
          _disabled: 'ToggleCard.Root.soft.elevated.bg.default/40',
          _notDisabled: {
            _hover: 'ToggleCard.Root.soft.elevated.bg.hover',
            _active: 'ToggleCard.Root.soft.elevated.bg.pressed',
          },
        },
      },
    },
    {
      selected: true,
      tone: 'neutral',
      css: {
        backgroundColor: {
          base: 'ToggleCard.Root.neutral.bg.selected.default',
          _disabled: 'ToggleCard.Root.neutral.bg.selected.default/40',
          _notDisabled: {
            _hover: 'ToggleCard.Root.neutral.bg.selected.hover',
            _active: 'ToggleCard.Root.neutral.bg.selected.pressed',
          },
        },
      },
    },
    {
      selected: true,
      tone: 'soft',
      css: {
        backgroundColor: {
          base: 'ToggleCard.Root.soft.bg.selected.default',
          _disabled: 'ToggleCard.Root.soft.bg.selected.default/40',
          _notDisabled: {
            _hover: 'ToggleCard.Root.soft.bg.selected.hover',
            _active: 'ToggleCard.Root.soft.bg.selected.pressed',
          },
        },
      },
    },
    {
      elevated: true,
      selected: true,
      tone: 'neutral',
      css: {
        backgroundColor: {
          base: 'ToggleCard.Root.neutral.elevated.bg.selected.default',
          _disabled: 'ToggleCard.Root.neutral.elevated.bg.selected.default/40',
          _notDisabled: {
            _hover: 'ToggleCard.Root.neutral.elevated.bg.selected.hover',
            _active: 'ToggleCard.Root.neutral.elevated.bg.selected.pressed',
          },
        },
      },
    },
    {
      elevated: true,
      selected: true,
      tone: 'soft',
      css: {
        backgroundColor: {
          base: 'ToggleCard.Root.soft.elevated.bg.selected.default',
          _disabled: 'ToggleCard.Root.soft.elevated.bg.selected.default/40',
          _notDisabled: {
            _hover: 'ToggleCard.Root.soft.elevated.bg.selected.hover',
            _active: 'ToggleCard.Root.soft.elevated.bg.selected.pressed',
          },
        },
      },
    },
  ],
});
